/**
 * Call Graph Page
 * Issue #462: Alternative views for large call graphs
 *
 * Provides multiple views for exploring call graph data:
 * 1. Function Explorer - Sourcetrail-style navigation
 * 2. Function List - Searchable table view
 * 3. Dependency Matrix - Grid visualization for pattern detection
 * 4. Focused Subgraph - React Flow diagram for small extractions
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import type { Diagram } from '@veodiagram/shared';
import type { CallGraphData } from '@veodiagram/shared';
import { api } from '../services/api';
import {
  FunctionExplorer,
  FunctionList,
  DependencyMatrix,
  FocusedSubgraph,
  TreeView,
} from '../components/callGraph';
import type { CallGraphNode, CallGraphEdge, FunctionNodeType } from '@veodiagram/shared';

type ViewMode = 'explorer' | 'list' | 'matrix' | 'subgraph' | 'tree';

/**
 * Convert stored DiagramGraph format back to CallGraphData format.
 * The generator outputs CallGraphData but queue.ts converts it to DiagramGraph for storage.
 * This function reverses that conversion for the Explorer components.
 */
function convertStoredGraphToCallGraphData(storedGraph: Record<string, unknown>): CallGraphData | null {
  const nodes = storedGraph.nodes as Array<Record<string, unknown>> | undefined;
  const edges = storedGraph.edges as Array<Record<string, unknown>> | undefined;

  if (!nodes || !edges) return null;

  // Convert nodes from DiagramGraph format to CallGraphData format
  const convertedNodes: CallGraphNode[] = nodes.map((node) => {
    const data = (node.data || {}) as Record<string, unknown>;

    // Determine name: prefer data.qualifiedName short form, then label, then id
    const qualifiedName = (data.qualifiedName as string) || (node.label as string) || (node.id as string);
    const name = (node.label as string) || qualifiedName.split('::').pop() || qualifiedName.split('.').pop() || (node.id as string);

    return {
      id: node.id as string,
      name,
      qualifiedName,
      module: (data.module as string) || 'unknown',
      type: ((data.functionType as string) || (node.type as string) || 'function') as FunctionNodeType,
      isAsync: (data.isAsync as boolean) || false,
      isEntryPoint: (data.isEntryPoint as boolean) || false,
      isExternal: (data.isExternal as boolean) || false,
      description: node.description as string | undefined,
      complexity: data.complexity as { cyclomatic: number; branches: number; loops: number } | undefined,
      location: data.location as { file: string; startLine: number; endLine: number } | undefined,
      calledCount: (data.calledCount as number) || 0,
      callCount: (data.callCount as number) || 0,
      evidence: data.evidence as CallGraphNode['evidence'],
      data: data,
    };
  });

  // Convert edges from DiagramGraph format to CallGraphData format
  const convertedEdges: CallGraphEdge[] = edges.map((edge) => {
    const data = (edge.data || {}) as Record<string, unknown>;

    return {
      id: edge.id as string,
      source: edge.source as string,
      target: edge.target as string,
      isAsync: (edge.animated as boolean) || false,
      isMethodCall: (data.isMethodCall as boolean) || false,
      callCount: (data.callCount as number) || 1,
      label: edge.label as string | undefined,
      callSites: (data.callSites as number[]) || [],
      confidence: (data.confidence as number) || 1.0,
      evidence: data.evidence as CallGraphEdge['evidence'],
      data: data,
    };
  });

  return {
    metadata: {
      title: 'Call Graph',
      diagramType: 'callGraph' as const,
      c4Level: 'code' as const,
    },
    nodes: convertedNodes,
    edges: convertedEdges,
    entryPoints: (storedGraph.entryPoints as string[]) || [],
    leafFunctions: (storedGraph.leafFunctions as string[]) || [],
    criticalPaths: (storedGraph.criticalPaths as CallGraphData['criticalPaths']) || [],
    hotspots: (storedGraph.hotspots as CallGraphData['hotspots']) || [],
    unusedFunctions: (storedGraph.unusedFunctions as string[]) || [],
    stats: storedGraph.stats as CallGraphData['stats'],
  };
}

export function CallGraphPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const [_diagram, setDiagram] = useState<Diagram | null>(null);
  const [callGraphData, setCallGraphData] = useState<CallGraphData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('explorer');
  const [selectedFunctionId, setSelectedFunctionId] = useState<string | null>(null);
  const [subgraphRootId, setSubgraphRootId] = useState<string | null>(null);
  const [subgraphHistory, setSubgraphHistory] = useState<string[]>([]);
  const [moduleFilter, setModuleFilter] = useState<string>('');
  const [typeFilters, setTypeFilters] = useState<Set<FunctionNodeType>>(new Set());
  // Entry-points dropdown (click the count in the stats bar to pick one)
  const [entryPointsOpen, setEntryPointsOpen] = useState(false);

  // Resolve the entry-point node IDs to displayable nodes (name + module),
  // sorted by name, for the stats-bar dropdown.
  const entryPointNodes = useMemo(() => {
    if (!callGraphData) return [];
    const byId = new Map(callGraphData.nodes.map(n => [n.id, n]));
    return (callGraphData.entryPoints ?? [])
      .map(id => byId.get(id))
      .filter((n): n is (typeof callGraphData.nodes)[number] => !!n)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [callGraphData]);

  // Close the entry-points dropdown on Escape (the backdrop handles mouse dismiss).
  useEffect(() => {
    if (!entryPointsOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setEntryPointsOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [entryPointsOpen]);

  // Compute available modules from raw data
  const modules = useMemo(() => {
    if (!callGraphData) return [];
    const s = new Set<string>();
    for (const n of callGraphData.nodes) s.add(n.module);
    return Array.from(s).sort();
  }, [callGraphData]);

  // Apply module + type filters to produce filtered call graph data for views
  const filteredCallGraphData = useMemo((): CallGraphData | null => {
    if (!callGraphData) return null;
    if (!moduleFilter && typeFilters.size === 0) return callGraphData;

    const filteredNodes = callGraphData.nodes.filter(n => {
      if (moduleFilter && n.module !== moduleFilter) return false;
      if (typeFilters.size > 0 && !typeFilters.has(n.type)) return false;
      return true;
    });
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredEdges = callGraphData.edges.filter(
      e => nodeIds.has(e.source) && nodeIds.has(e.target)
    );
    return { ...callGraphData, nodes: filteredNodes, edges: filteredEdges };
  }, [callGraphData, moduleFilter, typeFilters]);

  // Fetch call graph data
  const fetchCallGraph = useCallback(async () => {
    if (!projectId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get all diagrams for the project (metadata-only post-#1034)
      const diagrams = await api.getDiagrams(projectId);

      // Find the call graph diagram (latest version)
      const callGraphMeta = diagrams.find(
        (d) => d.type === 'callGraph' && d.isLatest !== false
      );

      if (!callGraphMeta) {
        setError('No call graph found. Run analysis to generate one.');
        return;
      }

      // #1040: list endpoint is metadata-only; fetch the full diagram (with
      // `graph`) by ID. Single-diagram GET still returns the heavy column.
      const callGraphDiagram = await api.getDiagram(callGraphMeta.id);
      setDiagram(callGraphDiagram);

      // Extract and convert the stored graph data to CallGraphData format
      // The data is stored in DiagramGraph format (React Flow), so we need to convert it
      const storedGraph = callGraphDiagram.graph as Record<string, unknown>;

      if (!storedGraph || !storedGraph.nodes || !storedGraph.edges) {
        setError('Call graph data is incomplete. Try regenerating the diagram.');
        return;
      }

      // Convert from DiagramGraph format back to CallGraphData format
      const normalizedData = convertStoredGraphToCallGraphData(storedGraph);

      if (!normalizedData) {
        setError('Failed to parse call graph data. Try regenerating the diagram.');
        return;
      }

      setCallGraphData(normalizedData);

      // Set initial selected function to the first entry point
      if (normalizedData.entryPoints && normalizedData.entryPoints.length > 0) {
        setSelectedFunctionId(normalizedData.entryPoints[0]);
      } else if (normalizedData.nodes.length > 0) {
        setSelectedFunctionId(normalizedData.nodes[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch call graph:', err);
      setError(err instanceof Error ? err.message : 'Failed to load call graph');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchCallGraph();
  }, [fetchCallGraph]);

  // #1074 C1: when navigated here from AnalysisResultsPage with a
  // `?rootFile=<path>` query param, auto-open the subgraph view rooted on
  // the first function defined in that file.
  //
  // CallGraphNode carries a `module` field which is set in
  // `apps/api/src/services/analysis/ast/graph/call-graph.ts` as the relative
  // path WITHOUT extension (e.g. `apps/api/src/foo/bar`), while the source
  // `filePath` carries the extension (e.g. `apps/api/src/foo/bar.ts`). Strip
  // source extensions from the incoming `rootFile` before suffix-matching,
  // otherwise this loop quietly never matches and the user lands on the
  // default explorer view (R1 catch on PR #1077).
  useEffect(() => {
    const rootFile = searchParams.get('rootFile');
    if (!rootFile || !callGraphData) return;
    const decoded = decodeURIComponent(rootFile)
      .toLowerCase()
      .replace(/\.(tsx?|jsx?|mjs|cjs|vue)$/, '');
    const firstNode = callGraphData.nodes.find(n => {
      const mod = (n.module || '').toLowerCase();
      return mod && (mod === decoded || decoded.endsWith(mod) || mod.endsWith(decoded));
    });
    if (firstNode) {
      setSubgraphRootId(firstNode.id);
      setSubgraphHistory([]);
      setViewMode('subgraph');
    }
  }, [callGraphData, searchParams]);

  // Handle function selection from any view
  const handleSelectFunction = useCallback((functionId: string) => {
    setSelectedFunctionId(functionId);
  }, []);

  // Handle "View in Explorer" action from List view
  const handleViewInExplorer = useCallback((functionId: string) => {
    setSelectedFunctionId(functionId);
    setViewMode('explorer');
  }, []);

  // Handle opening focused subgraph view
  const handleFocusFunction = useCallback((functionId: string) => {
    setSubgraphRootId(functionId);
    setSubgraphHistory([]);
    setViewMode('subgraph');
  }, []);

  // Handle edge selection from matrix view
  const handleSelectEdge = useCallback((sourceId: string, _targetId: string) => {
    // Switch to explorer view and show the source function
    setSelectedFunctionId(sourceId);
    setViewMode('explorer');
  }, []);

  // Navigate back in subgraph history
  const handleSubgraphBack = useCallback(() => {
    if (subgraphHistory.length > 0) {
      const prev = subgraphHistory[subgraphHistory.length - 1];
      setSubgraphHistory(h => h.slice(0, -1));
      setSubgraphRootId(prev);
    }
  }, [subgraphHistory]);

  // Jump to a specific point in the subgraph navigation history
  const handleSubgraphNavigateToIndex = useCallback((i: number) => {
    const target = subgraphHistory[i];
    if (target) {
      setSubgraphHistory(prev => prev.slice(0, i));
      setSubgraphRootId(target);
    }
  }, [subgraphHistory]);

  // Close subgraph view
  const handleCloseSubgraph = useCallback(() => {
    setViewMode('explorer');
    setSubgraphRootId(null);
    setSubgraphHistory([]);
  }, []);


  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          <p className="text-slate-600 dark:text-slate-400">Loading call graph...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
            Error Loading Call Graph
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={fetchCallGraph}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
            <Link
              to={`/projects/${projectId}`}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Back to Project
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!callGraphData) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-slate-600 dark:text-slate-400">No call graph data available</p>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-50 dark:bg-gray-900 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to={`/projects/${projectId}`}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              ← Back to Project
            </Link>
            <h1 className="text-xl font-semibold text-slate-800 dark:text-white">
              Call Graph Explorer
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {/* View mode tabs */}
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('explorer')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  viewMode === 'explorer'
                    ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400 font-medium'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Explorer
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400 font-medium'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                List
              </button>
              <button
                onClick={() => setViewMode('matrix')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  viewMode === 'matrix'
                    ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400 font-medium'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Matrix
              </button>
              <button
                onClick={() => setViewMode('tree')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  viewMode === 'tree'
                    ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400 font-medium'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Tree
              </button>
              <button
                onClick={() => {
                  setSubgraphRootId(selectedFunctionId);
                  setViewMode('subgraph');
                }}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  viewMode === 'subgraph'
                    ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400 font-medium'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Subgraph
              </button>
            </div>
            <button
              onClick={fetchCallGraph}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>
        </div>
      </header>

      {/* Stats bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6 py-2 flex items-center gap-4 text-sm flex-wrap">
        <span className="text-slate-600 dark:text-slate-400">
          <strong className="text-slate-800 dark:text-white">{filteredCallGraphData?.nodes.length ?? 0}</strong>
          {(moduleFilter || typeFilters.size > 0) && <span className="text-slate-400"> / {callGraphData.nodes.length}</span>}
          {' '}functions
        </span>
        <span className="text-slate-600 dark:text-slate-400">
          <strong className="text-slate-800 dark:text-white">{filteredCallGraphData?.edges.length ?? 0}</strong> calls
        </span>
        {/* Entry points — click the count to pick one and open it in the explorer */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setEntryPointsOpen(o => !o)}
            disabled={entryPointNodes.length === 0}
            aria-expanded={entryPointsOpen}
            aria-haspopup="menu"
            title={entryPointNodes.length ? 'Show entry points' : 'No entry points'}
            className="text-slate-600 dark:text-slate-400 enabled:hover:text-blue-600 dark:enabled:hover:text-blue-400 disabled:cursor-default inline-flex items-center gap-1"
          >
            <strong className="text-slate-800 dark:text-white">{callGraphData.entryPoints?.length || 0}</strong> entry points
            {entryPointNodes.length > 0 && (
              <svg className={`w-3 h-3 transition-transform ${entryPointsOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            )}
          </button>
          {entryPointsOpen && entryPointNodes.length > 0 && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setEntryPointsOpen(false)} />
              <div role="menu" className="absolute left-0 top-full mt-1 z-20 w-96 max-w-[90vw] max-h-80 overflow-auto rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1">
                <div className="px-3 py-1.5 text-xs font-medium text-slate-400 border-b border-slate-100 dark:border-gray-700">
                  {entryPointNodes.length} entry point{entryPointNodes.length === 1 ? '' : 's'} — select to open in explorer
                </div>
                {entryPointNodes.map(n => (
                  <button
                    key={n.id}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      // Clear active filters so the Explorer's filtered dataset
                      // definitely contains the picked node (the dropdown lists
                      // all entry points regardless of the current filters).
                      setModuleFilter('');
                      setTypeFilters(new Set());
                      handleViewInExplorer(n.id);
                      setEntryPointsOpen(false);
                    }}
                    className="flex w-full items-baseline gap-2 px-3 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-gray-700"
                    title={n.qualifiedName || n.name}
                  >
                    <span className="font-medium text-slate-800 dark:text-white truncate">{n.name}</span>
                    <span className="text-xs text-slate-400 truncate ml-auto">{n.module}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        {callGraphData.stats && (
          <span className="text-slate-600 dark:text-slate-400">
            Max depth: <strong className="text-slate-800 dark:text-white">{callGraphData.stats.maxCallDepth}</strong>
          </span>
        )}

        <div className="flex-1" />

        {/* Filters — dimmed for Explorer and Subgraph views where they don't apply cleanly */}
        <div className={`flex items-center gap-2 flex-wrap transition-opacity ${(viewMode === 'explorer' || viewMode === 'subgraph') ? 'opacity-40 pointer-events-none' : ''}`}>
          {/* Module filter */}
          <select
            value={moduleFilter}
            onChange={e => setModuleFilter(e.target.value)}
            className="px-2 py-1 border border-slate-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
            title="Filter by module"
          >
            <option value="">All modules</option>
            {modules.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          {/* Type filter chips */}
          {(['function', 'method', 'arrow', 'constructor', 'entry', 'external'] as FunctionNodeType[]).map(t => {
            const active = typeFilters.has(t);
            return (
              <button
                key={t}
                onClick={() => setTypeFilters(prev => {
                  const next = new Set(prev);
                  if (next.has(t)) next.delete(t); else next.add(t);
                  return next;
                })}
                className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-700 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-gray-600 hover:border-slate-400'}`}
                title={`Filter by type: ${t}`}
              >
                {t}
              </button>
            );
          })}

          {(moduleFilter || typeFilters.size > 0) && (
            <button
              onClick={() => { setModuleFilter(''); setTypeFilters(new Set()); }}
              className="px-2 py-0.5 text-xs text-blue-600 hover:underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Explain why filters are dimmed on Explorer and Subgraph tabs */}
      {(viewMode === 'explorer' || viewMode === 'subgraph') && (
        <div className="px-6 py-1.5 bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 text-xs text-slate-500 dark:text-slate-400 italic">
          {viewMode === 'subgraph'
            ? "Filters apply to List, Matrix and Tree views. Applying them in Subgraph view would silently truncate the BFS traversal. Use the subgraph's own controls (direction, depth, hide external) instead."
            : 'Filters apply to List, Matrix and Tree views. The Explorer navigates the full call graph regardless of active filters.'}
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'explorer' && filteredCallGraphData && (
          <FunctionExplorer
            data={filteredCallGraphData}
            selectedFunctionId={selectedFunctionId || undefined}
            onSelectFunction={handleSelectFunction}
            onFocusFunction={handleFocusFunction}
          />
        )}

        {viewMode === 'list' && filteredCallGraphData && (
          <FunctionList
            data={filteredCallGraphData}
            selectedFunctionId={selectedFunctionId || undefined}
            onSelectFunction={handleSelectFunction}
            onViewInExplorer={handleViewInExplorer}
            onViewSubgraph={handleFocusFunction}
          />
        )}

        {viewMode === 'matrix' && filteredCallGraphData && (
          <DependencyMatrix
            data={filteredCallGraphData}
            onSelectFunction={handleSelectFunction}
            onSelectEdge={handleSelectEdge}
          />
        )}

        {viewMode === 'tree' && filteredCallGraphData && (
          <TreeView
            data={filteredCallGraphData}
            selectedFunctionId={selectedFunctionId || undefined}
            onSelectFunction={handleSelectFunction}
            onViewInExplorer={handleViewInExplorer}
            onViewSubgraph={handleFocusFunction}
          />
        )}

        {viewMode === 'subgraph' && subgraphRootId && filteredCallGraphData && (
          <FocusedSubgraph
            data={filteredCallGraphData}
            rootFunctionId={subgraphRootId}
            onSelectFunction={(id) => {
              setSubgraphHistory(h => [...h, subgraphRootId!]);
              setSubgraphRootId(id);
            }}
            onBack={subgraphHistory.length > 0 ? handleSubgraphBack : undefined}
            onClose={handleCloseSubgraph}
            historyFunctionIds={subgraphHistory}
            onNavigateToHistoryIndex={handleSubgraphNavigateToIndex}
          />
        )}

        {viewMode === 'subgraph' && !subgraphRootId && (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <p className="text-lg mb-4">Select a function to view its subgraph</p>
            <button
              onClick={() => setViewMode('explorer')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Go to Explorer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
